import { urlBuilder } from '@starjack/utils';
import { BackendType } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import { Billing, convertBillingToFrontend } from '../model/billing.types';

const billingUrls = {
    billing: urlBuilder('order', 'billing'),
};

/**
 * Type definition for the API with injected billing hooks
 */
export interface BillingApiHooks {
    useGetBillingsQuery: (params: { liftOperatorId: string }) => {
        data?: Billing[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePostReleaseMutation: () => [
        (params: { billingId: string }) => { unwrap: () => Promise<Billing> },
        { isLoading: boolean; error?: unknown },
    ];
}

/**
 * Billing endpoint definitions
 */
export const billingEndpoints = (builder: EntityEndpointBuilder) => ({
    getBillings: builder.query<Billing[], { liftOperatorId: string }>({
        query: () => billingUrls.billing,
        transformResponse: (apiReturn: {
            billingRuns: Billing<BackendType>[];
        }) => apiReturn.billingRuns.map(convertBillingToFrontend),
        providesTags: ['BILLINGS'],
    }),

    postRelease: builder.mutation<Billing, { billingId: string }>({
        query: ({ billingId }) => ({
            url: `${billingUrls.billing}/${billingId}/release`,
            method: 'POST',
            body: undefined,
        }),
        invalidatesTags: ['BILLINGS'],
    }),
});

export const billingApi = createEntityApi({
    tagTypes: ['BILLINGS'],
    endpoints: billingEndpoints,
});

// Export hooks with explicit types for TypeScript inference
export const useGetBillingsQuery: BillingApiHooks['useGetBillingsQuery'] =
    billingApi.useGetBillingsQuery;
export const usePostReleaseMutation: BillingApiHooks['usePostReleaseMutation'] =
    billingApi.usePostReleaseMutation;
